import React, { Component } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import moment from "moment";
import { FormControl, FormGroup, FormLabel } from "react-bootstrap";
import * as timesActions from "actions/timesActions";
import { timesService, employeesService } from "services";
import { withTranslation } from "react-i18next";
import {
	getWorkedTimeCounter,
	getTimeCounterByType,
	formatMinutesToTime,
} from "utils/datetime";
import { getEmployeeTimeTypes } from "components/TimeTypes/utils";

import { errorFormating } from "utils/utils";
import Counter from "components/Counter";
import CounterStatic from "components/CounterStatic";
import IconSelect from "components/IconSelect";
import eventBus, { DASHBOARD_EVENTS } from "utils/eventBus";
import { trackHaddockEmployeeClockIn } from "utils/posthogTracking";
import {
	getTimeChatId,
	sendTimeRegistrationChatMessage,
} from "utils/timeChatUtils";
import {
	defaultUbicationId,
	resolveUbicationId,
	toSelectOptions,
	withUbicationId,
} from "components/TimeRegistrations/timeRegistrationUbication";
import { FiSquare, FiPause, FiPlay, FiMapPin, FiBriefcase } from "react-icons/fi";
import {
	findPauseTimeType,
	formatClockinHms,
	formatGoalElapsed,
	getDayProgressPercent,
	getWorkdayTargetHours,
	getWorkedSecondsIncludingOngoing,
	parseTimeCounter,
} from "components/Dashboard/clockinWidget";

const MAX_YESTERDAY_ONGOING_HOURS = 16;

class Dashboard_Clockin extends Component {
	constructor(props) {
		super(props);
		// console.log(props);
		this.state = {
			isRecording: false,
			today_times: [],
			ongoing_time: {},
			clockin_error: "",
			comments: "",
			total_counter: 0,
			isLoading: false,
			stale_yesterday_ongoing: null,
			ubication_id: -1,
			employee_ubications_list: [],
			commentsOpen: false,
			nowTick: Date.now(),
		};

		this.refreshInterval = null;
		this.tickInterval = null;
		this.commentsInputRef = React.createRef();
	}

	async componentDidMount() {
		this.props.getSelectableTimeTypes();
		await this.loadEmployeeUbications();
		this.getTodayTimes();

		// TODO: Reemplazar por WebSocket cuando el backend emita eventos de fichaje
		this.refreshInterval = setInterval(() => {
			this.getTodayTimes();
		}, 60000); // Actualiza cada 60 segundos

		this.tickInterval = setInterval(() => {
			if (this.state.isRecording) {
				this.setState({ nowTick: Date.now() });
			}
		}, 1000);
	}

	componentWillUnmount() {
		clearInterval(this.refreshInterval);
		clearInterval(this.tickInterval);
	}

	handleChange = (event) => {
		const { id, value } = event.target;
		const parsedValue =
			id === "ubication_id" || id === "type_id"
				? parseInt(value, 10)
				: value;

		this.setState({
			[id]: parsedValue,
		});
	};

	openComments = () => {
		this.setState({ commentsOpen: true }, () => {
			this.commentsInputRef.current?.focus();
		});
	};

	renderStatusPill = (status) => {
		const { t } = this.props;
		const labels = {
			idle: t("widgets.clockin.statusIdle"),
			active: t("widgets.clockin.statusActive"),
			paused: t("widgets.clockin.pausedBadge"),
		};

		return (
			<span className={`clockin-status clockin-status--${status}`}>
				<span className="clockin-status-dot" aria-hidden="true" />
				{labels[status]}
			</span>
		);
	};

	renderGoalBar = (workedSeconds) => {
		const { t } = this.props;
		const progressPercent = getDayProgressPercent(workedSeconds);
		const targetHours = getWorkdayTargetHours();

		return (
			<div className="clockin-goal">
				<div
					className="clockin-progress"
					role="progressbar"
					aria-valuemin={0}
					aria-valuemax={100}
					aria-valuenow={Math.round(progressPercent)}
					aria-label={t("widgets.clockin.hoursWorkedToday")}
				>
					<div
						className="clockin-progress-fill"
						style={{ width: `${progressPercent}%` }}
					/>
				</div>
				<div className="clockin-goal-label">
					<span>{formatGoalElapsed(workedSeconds)}</span>
					<span>{t("widgets.clockin.goalTarget", { hours: targetHours })}</span>
				</div>
			</div>
		);
	};

	loadEmployeeUbications = async () => {
		const userId = this.props.user?.id;
		if (!userId) return;

		const response = await employeesService.getUbications(userId);
		this.setState({
			employee_ubications_list: response.ok ? response : [],
		});
	};

	componentDidUpdate(prevProps) {
		if (prevProps.user?.id !== this.props.user?.id && this.props.user?.id) {
			this.loadEmployeeUbications();
		}
	}

	getUbicationOptionsList = () => {
		const { employee_ubications_list } = this.state;
		return toSelectOptions(employee_ubications_list, []);
	};

	getDefaultUbicationSelectValue = () => {
		const options = this.getUbicationOptionsList();
		return defaultUbicationId(options, null);
	};

	resolveUbicationSelectValue = (time) => {
		const resolved = resolveUbicationId(time);
		if (resolved != null) return resolved;
		return this.getDefaultUbicationSelectValue();
	};

	async getTodayTimes() {
		const userId = this.props.user.id;
		const today = moment().format("YYYY-MM-DD");
		const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");

		// Buscar fichajes del día actual
		const todayResponse = await timesService.getAll({
			employee_id: userId,
			start_from: today,
			start_to: today,
			page: 0,
		});

		// Buscar fichajes en curso del día anterior (para turnos nocturnos)
		const yesterdayResponse = await timesService.getAll({
			employee_id: userId,
			start_from: yesterday,
			start_to: yesterday,
			page: 0,
		});

		let timeRegistrations = [];
		let ongoingTime = null;
		let staleYesterdayOngoing = null;

		// Procesar fichajes del día actual
		if (
			todayResponse.ok &&
			todayResponse.data &&
			todayResponse.data.length > 0
		) {
			timeRegistrations = todayResponse.data;
			ongoingTime = timeRegistrations.find((time) => !time.end);
		}

		// Si no hay fichaje en curso hoy, buscar en fichajes de ayer (turnos nocturnos)
		if (
			!ongoingTime &&
			yesterdayResponse.ok &&
			yesterdayResponse.data &&
			yesterdayResponse.data.length > 0
		) {
			const yesterdayOngoing = yesterdayResponse.data.find((time) => !time.end);
			if (yesterdayOngoing) {
				const elapsedHours = moment().diff(
					moment.utc(yesterdayOngoing.start),
					"hours",
					true
				);

				if (elapsedHours <= MAX_YESTERDAY_ONGOING_HOURS) {
					ongoingTime = yesterdayOngoing;
					// Agregar el fichaje de ayer al principio de los registros
					timeRegistrations = [yesterdayOngoing, ...timeRegistrations];
				} else {
					// Si supera el límite nocturno, no lo tratamos como fichaje activo en dashboard
					staleYesterdayOngoing = yesterdayOngoing;
				}
			}
		}

		// Actualizar estado según si hay fichaje en curso o no
		if (ongoingTime) {
			// Hay un fichaje en curso (sin hora de salida)
			this.setState({
				ongoing_time: ongoingTime,
				today_times: timeRegistrations,
				isRecording: true,
				type_id: ongoingTime.type.id,
				ubication_id: this.resolveUbicationSelectValue(ongoingTime),
				total_counter: 0,
				stale_yesterday_ongoing: null,
			});
		} else if (timeRegistrations.length > 0) {
			// Hay fichajes pero todos están cerrados
			this.setState({
				ongoing_time: {},
				isRecording: false,
				today_times: timeRegistrations,
				total_counter: getWorkedTimeCounter(timeRegistrations),
				type_id: this.props.default_time_type?.id || null,
				ubication_id: this.getDefaultUbicationSelectValue(),
				stale_yesterday_ongoing: staleYesterdayOngoing,
			});
		} else {
			// No hay fichajes hoy: dejamos todo en blanco con el tipo por defecto
			this.setState({
				ongoing_time: {},
				isRecording: false,
				today_times: [],
				total_counter: 0,
				type_id: this.props.default_time_type?.id || null,
				ubication_id: this.getDefaultUbicationSelectValue(),
				stale_yesterday_ongoing: staleYesterdayOngoing,
			});
		}
	}

	async handleClockIn() {
		this.setState({ isLoading: true });
		try {
			const { ongoing_time, type_id, comments, ubication_id } = this.state;
			const userId = this.props.user.id;

			// update time (fichaje salida)
			if (ongoing_time.id !== undefined) {
				const time = withUbicationId(
					{
						id: ongoing_time.id,
						start: moment.utc(ongoing_time.start).format(),
						end: moment().utc().format(),
						type_id: ongoing_time.type.id,
						observations: ongoing_time.observations,
						approved: false,
						employee_id: userId,
						start_source: ongoing_time.start_source || "HOMEPAGE_WIDGET",
						end_source: "HOMEPAGE_WIDGET",
					},
					ongoing_time,
					{ echo: true }
				);

				const response = await timesService.update(time.id, time);

				// Emitir evento de fichaje de salida completado
				if (response.ok) {
					await sendTimeRegistrationChatMessage(
						ongoing_time.id,
						comments,
						getTimeChatId(ongoing_time)
					);

					this.setState({
						ongoing_time: {},
						isRecording: false,
						clockin_error: "",
						comments: "",
					});

					trackHaddockEmployeeClockIn(this.props.user, {
						employee_id: userId,
						clock_action: "salida",
						source: "dashboard_widget",
					});

					eventBus.emit(DASHBOARD_EVENTS.CLOCK_OUT_COMPLETED, {
						time: response,
						userId: userId,
						timestamp: new Date(),
					});
					eventBus.emit(DASHBOARD_EVENTS.TIME_REGISTRATION_UPDATED, {
						type: "clock_out",
						userId: userId,
						timestamp: new Date(),
					});
				} else {
					// si hay error, mantenemos el fichaje en curso para que siga el contador
					this.setState({
						clockin_error: errorFormating(response),
						ongoing_time: ongoing_time,
						isRecording: true,
					});
				}
				// Refresh today times to update counters
				this.getTodayTimes();
			}
			// new time (fichaje entrada)
			else {
				const time = withUbicationId(
					{
						approved: false,
						employee_id: userId,
						type_id,
						start: moment().utc().format(),
						start_source: "HOMEPAGE_WIDGET",
						end_source: "HOMEPAGE_WIDGET",
					},
					ubication_id ?? -1,
					{ echo: false }
				);
				const response = await timesService.add(time);

				if (response.ok) {
					await sendTimeRegistrationChatMessage(
						response.id,
						comments,
						getTimeChatId(response)
					);

					this.setState({
						ongoing_time: response,
						isRecording: true,
						comments: "",
						commentsOpen: false,
					});

					trackHaddockEmployeeClockIn(this.props.user, {
						employee_id: userId,
						clock_action: "entrada",
						source: "dashboard_widget",
					});

					// Emitir evento de fichaje de entrada completado
					eventBus.emit(DASHBOARD_EVENTS.CLOCK_IN_COMPLETED, {
						time: response,
						userId: userId,
						timestamp: new Date(),
					});
					eventBus.emit(DASHBOARD_EVENTS.TIME_REGISTRATION_UPDATED, {
						type: "clock_in",
						userId: userId,
						timestamp: new Date(),
					});
				} else {
					this.setState({
						clockin_error: errorFormating(response),
					});
				}
			}
		} catch (error) {
			console.error("Error in handleClockIn:", error);
			this.setState({
				clockin_error: this.props.t("widgets.clockin.unexpectedError"),
			});
		} finally {
			this.setState({ isLoading: false });
		}
	}

	getClockIn = () => {
		const { t } = this.props;
		const { hours, minutes, seconds } = parseTimeCounter(
			this.state.total_counter
		);

		const { select_time_types, default_time_type, user } = this.props;
		const {
			comments,
			commentsOpen,
			stale_yesterday_ongoing,
			ubication_id,
			today_times,
		} = this.state;
		const showComments = commentsOpen;
		const workedSeconds = getWorkedSecondsIncludingOngoing(today_times);

		const ubicationOptions = this.getUbicationOptionsList();
		const ubicationSelectOptions =
			ubicationOptions.length > 0 &&
			ubicationOptions.map((item) => ({
				value: item.id,
				label: item.name,
				icon: <FiMapPin />,
			}));

		const time_types_list =
			user.employee_with_time_types && user.employee_with_time_types.length > 0
				? getEmployeeTimeTypes(user.employee_with_time_types)
				: select_time_types;

		const timeTypeSelectOptions =
			time_types_list.length > 0 &&
			time_types_list.map((item) => ({
				value: item.id,
				label: item.name,
				icon: <FiBriefcase />,
			}));

		let hasOthers = false;

		// mostra altres tipus de temps si n'hi ha i si no són el default de treball
		let typesCounterList =
			time_types_list.length > 0 &&
			default_time_type &&
			time_types_list.map((time_type, i) => {
				if (default_time_type.id !== time_type.id) {
					let count = getTimeCounterByType(this.state.today_times, time_type);
					const formatedTime = formatMinutesToTime(count);
					if (count > 0) {
						hasOthers = true;

						return (
							<li key={i} value={time_type.id}>
								{time_type.name} <strong>{formatedTime}</strong>
							</li>
						);
					}
				}
			}, this);

		return (
			<article className="card clockin clockin--idle">
				<div className="card-top">
					<h2>{t("widgets.clockin.title")}</h2>
					{this.renderStatusPill("idle")}
				</div>
				<div className="card-content">
					<CounterStatic hours={hours} minutes={minutes} seconds={seconds} />
					<span className="label-day">{t("widgets.clockin.workdayToday")}</span>
					{this.renderGoalBar(workedSeconds)}
				</div>

				{today_times.length > 0 && hasOthers && (
					<>
						<h3 className="resumen-title">{t("widgets.clockin.others")}</h3>
						<ul className="resumen-list">{typesCounterList}</ul>
					</>
				)}

				{this.state.clockin_error.start !== undefined && (
					<div
						className="alert alert-danger alert-dismissible fade show"
						role="alert"
					>
						{this.state.clockin_error.start}
					</div>
				)}
				{stale_yesterday_ongoing && (
					<div className="alert alert-warning" role="alert">
						Fichaje abierto desde el{" "}
						<strong>
							{moment
								.utc(stale_yesterday_ongoing.start)
								.local()
								.format("DD/MM HH:mm")}
							h
						</strong>{" "}
						que supera el limite de{" "}
						<strong>{MAX_YESTERDAY_ONGOING_HOURS}h</strong> y ya no aparece como
						activo. Puedes cerrarlo desde{" "}
						<Link to="/my-times">Mis fichajes</Link>.
					</div>
				)}

				<div className="clockin-idle-bottom">
					{ubicationOptions.length > 0 && (
						<FormGroup className="form-group clockin-field" controlId="ubication_id">
							<FormLabel>{t("widgets.clockin.ubicationLabel")}</FormLabel>
							<IconSelect
								id="ubication_id"
								value={ubication_id ?? -1}
								onChange={this.handleChange}
								icon={<FiMapPin />}
								placeholderOption={{
									value: -1,
									label: t("times.selectUbication"),
								}}
								options={ubicationSelectOptions || []}
							/>
						</FormGroup>
					)}

					<FormGroup className="form-group clockin-field" controlId="type_id">
						<FormLabel>{t("widgets.clockin.typeLabel")}</FormLabel>
						<IconSelect
							id="type_id"
							value={this.state.type_id || -1}
							onChange={this.handleChange}
							icon={<FiBriefcase />}
							placeholderOption={{
								value: -1,
								label: t("widgets.clockin.selectType"),
							}}
							options={timeTypeSelectOptions || []}
						/>
					</FormGroup>

					<button
						type="button"
						onClick={() => this.handleClockIn()}
						className="btn btn-primary clockin-btn clockin-btn--primary clockin-idle-submit"
						disabled={this.state.isLoading}
					>
						<FiPlay /> {t("widgets.clockin.clockIn")}
					</button>

					<div className="clockin-comment-block">
						{showComments ? (
							<FormGroup className="form-group mb0 clockin-comments" controlId="comments">
								<FormControl
									as="textarea"
									id="comments"
									ref={this.commentsInputRef}
									value={comments}
									className="comments"
									onChange={this.handleChange}
									placeholder={t("widgets.clockin.commentsPlaceholder")}
								/>
							</FormGroup>
						) : (
							<button
								type="button"
								className="clockin-comment-link"
								onClick={this.openComments}
							>
								<span className="clockin-comment-plus" aria-hidden="true">
									+
								</span>
								{t("widgets.clockin.addComment")}
							</button>
						)}
					</div>
				</div>
			</article>
		);
	};

	async switchTimeType(timeTypeId) {
		const { ongoing_time } = this.state;
		const userId = this.props.user.id;

		if (!ongoing_time.id) return;

		this.setState({ isLoading: true });
		try {
			// 1. Close current time registration
			const time = withUbicationId(
				{
					id: ongoing_time.id,
					start: moment.utc(ongoing_time.start).format(),
					end: moment().utc().format(),
					type_id: ongoing_time.type.id,
					observations: ongoing_time.observations,
					approved: false,
					employee_id: userId,
					start_source: ongoing_time.start_source || "HOMEPAGE_WIDGET",
					end_source: "HOMEPAGE_WIDGET",
				},
				ongoing_time,
				{ echo: true }
			);

			const closeResponse = await timesService.update(time.id, time);

			if (!closeResponse.ok) {
				this.setState({
					clockin_error: errorFormating(closeResponse),
				});
				return;
			}

			// Emit event for clock out
			eventBus.emit(DASHBOARD_EVENTS.CLOCK_OUT_COMPLETED, {
				time: closeResponse,
				userId: userId,
				timestamp: new Date(),
			});

			// 2. Start new time registration with the new type
			const newTime = withUbicationId(
				{
					start: moment().utc().format(),
					employee_id: userId,
					type_id: timeTypeId,
					approved: false,
					start_source: "HOMEPAGE_WIDGET",
					end_source: "HOMEPAGE_WIDGET",
				},
				resolveUbicationId(ongoing_time) ?? this.state.ubication_id ?? -1,
				{ echo: false }
			);

			const startResponse = await timesService.add(newTime);

			if (startResponse.ok) {
				this.setState({
					ongoing_time: startResponse,
					isRecording: true,
					type_id: timeTypeId, // Update the selected type in state
					clockin_error: "",
				});

				// Emit event for clock in
				eventBus.emit(DASHBOARD_EVENTS.CLOCK_IN_COMPLETED, {
					time: startResponse,
					userId: userId,
					timestamp: new Date(),
				});

				// General update event
				eventBus.emit(DASHBOARD_EVENTS.TIME_REGISTRATION_UPDATED, {
					type: "switch_time_type",
					userId: userId,
					timestamp: new Date(),
				});
			} else {
				this.setState({
					clockin_error: errorFormating(startResponse),
					// If starting the new one fails, we are in a weird state (old one closed, new one failed).
					// We might want to re-open the old one or just let the user know.
					// For now, just showing error and keeping the state as "not recording" or however getTodayTimes handles it on refresh.
					isRecording: false, // The user is technically not recording anymore since the previous one closed.
					ongoing_time: {},
				});
				// Ideally we should refresh from server to be sure
				this.getTodayTimes();
			}
		} catch (error) {
			console.error("Error in switchTimeType:", error);
			this.setState({
				clockin_error: this.props.t("widgets.clockin.unexpectedErrorSwitch"),
			});
		} finally {
			this.setState({ isLoading: false });
		}
	}

	getOnGoingClockIn = () => {
		const { t } = this.props;
		const { ongoing_time, today_times, nowTick } = this.state;
		const { select_time_types, user, default_time_type } = this.props;
		const hora = moment.utc(ongoing_time.start).local().format("H:mm");

		const time_types_list =
			user.employee_with_time_types && user.employee_with_time_types.length > 0
				? getEmployeeTimeTypes(user.employee_with_time_types)
				: select_time_types;

		let pauseType = findPauseTimeType(time_types_list);

		if (!pauseType) {
			pauseType = findPauseTimeType(select_time_types);
		}

		const isPaused =
			pauseType && ongoing_time.type && ongoing_time.type.id === pauseType.id;

		const workedSeconds = getWorkedSecondsIncludingOngoing(
			today_times,
			moment(nowTick)
		);
		const workedToday = formatClockinHms(workedSeconds);

		return (
			<article
				className={`card clockin ${isPaused ? "clockin--paused" : "clockin--active"}`}
			>
				<div className="card-top">
					<h2>{t("widgets.clockin.title")}</h2>
					{this.renderStatusPill(isPaused ? "paused" : "active")}
				</div>
				<div className="card-content">
					<Counter start={ongoing_time.start} bStatic={false} />
					<div className="label-day clockin-worked-today">
						{!isPaused && (
							<div className="clockin-current-type">
								{t("widgets.clockin.ongoing", { type: ongoing_time.type.name })}
							</div>
						)}
						<div>
							{t("widgets.clockin.hoursWorkedToday")}:{" "}
							<strong>{workedToday}</strong>
						</div>
					</div>

					{this.renderGoalBar(workedSeconds)}

					<div className="clockin-start">
						<strong className="clockin-start-time">{hora}</strong>
						<span className="clockin-start-label">
							{t("widgets.clockin.startTime")}
						</span>
					</div>
				</div>

				{this.state.clockin_error.start !== undefined && (
					<div
						className="alert alert-danger alert-dismissible fade show"
						role="alert"
					>
						{this.state.clockin_error.start}
					</div>
				)}
				<div className="clockin-actions">
					<button
						type="button"
						onClick={() => this.handleClockIn()}
						className="btn clockin-btn clockin-btn--stop"
						disabled={this.state.isLoading}
					>
						<FiSquare /> {t("widgets.clockin.stop")}
					</button>

					{isPaused ? (
						<button
							type="button"
							onClick={() => {
								let targetTypeId = default_time_type?.id;
								if (!targetTypeId) {
									const workType = time_types_list.find(
										(type) => type.name.toLowerCase() === "trabajo"
									);
									targetTypeId = workType?.id;
								}
								if (targetTypeId) {
									this.switchTimeType(targetTypeId);
								} else {
									console.warn(
										"Cannot find a suitable time type to resume work."
									);
								}
							}}
							className="btn clockin-btn clockin-btn--resume"
							disabled={this.state.isLoading}
						>
							<FiPlay /> {t("widgets.clockin.resume")}
						</button>
					) : (
						pauseType && (
							<button
								type="button"
								onClick={() => this.switchTimeType(pauseType.id)}
								className="btn clockin-btn clockin-btn--primary"
								disabled={this.state.isLoading}
							>
								<FiPause /> {t("widgets.clockin.pause")}
							</button>
						)
					)}
				</div>
			</article>
		);
	};

	render() {
		return this.state.ongoing_time.id !== undefined
			? this.getOnGoingClockIn()
			: this.getClockIn();
	}
}

const mapStateToProps = (reducers) => {
	return {
		...reducers.timesReducer,
		...reducers.ubicationsReducer,
		user: reducers.authReducer.user,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getDefaultTimeType: () => dispatch(timesActions.getDefaultTimeType()),
	getSelectableTimeTypes: () => dispatch(timesActions.getSelectableTimeTypes()),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(withTranslation()(Dashboard_Clockin));
